/**
 * Field records (surveys, transects, quadrats, trees, regeneration plots and
 * counts, disturbances), saved on the device first and synced to the server.
 *
 * Writes never call the record endpoints directly. Each one becomes an
 * operation in the outbox (see syncEngine.js) and is applied to the device's
 * copy of the survey tree at once, so the page gets the result straight away,
 * with or without signal. New records use a client_id UUID as their `id` until
 * the server has them; links and further changes work with either id.
 *
 * Reads go to the server as before when online with nothing waiting to sync.
 * Otherwise they are answered from the device: the saved survey tree with the
 * waiting changes applied (offlineFallback.js builds each response shape).
 */
import { announce, cachedUser, get as networkGet } from './http';
import { RECORD_TYPES, applyOperations, findRecord, isClientId, recordData } from './localTree';
import { deriveFromSurveys } from './offlineFallback';
import { addToOutbox, countOutbox, readOutbox, readResponse } from './offlineStore';
import { scheduleSync } from './syncEngine';

const newId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const speciesNamer = async (userId) => {
  const saved = await readResponse(userId, '/species');
  const names = new Map((saved?.data || []).map((s) => [Number(s.id), s.name]));
  return (id) => names.get(Number(id));
};

/** The survey tree as the user should see it, or { view: null } if nothing is saved yet. */
export const localView = async (userId) => {
  const [saved, operations, speciesName] = await Promise.all([
    readResponse(userId, '/surveys'),
    readOutbox(userId),
    speciesNamer(userId),
  ]);
  if (!saved && !operations.length) return { view: null };
  return {
    view: applyOperations(saved?.data || [], operations, { speciesName }),
    savedAt: saved?.savedAt,
    pending: operations.length,
  };
};

const httpError = (status, message) => {
  const error = new Error(message);
  error.response = { status, data: { message } };
  return error;
};

const read = async (path) => {
  const user = cachedUser.get();
  if (!user?.id) return networkGet(path);

  const onDeviceOnly = path.split('/').some(isClientId); // the server doesn't know client ids
  const waiting = await countOutbox(user.id);
  if (!onDeviceOnly && !waiting && navigator.onLine !== false) return networkGet(path);

  const { view, savedAt } = await localView(user.id);
  if (!view) return networkGet(path); // nothing on the device yet; try the network
  const data = deriveFromSurveys(path, view);
  if (data === undefined) throw httpError(404, 'Not found');
  if (navigator.onLine === false) announce({ offline: true, savedAt });
  return data;
};

// The device's view, fetching the survey tree first if it was never saved.
// Records saved before sync existed have no client_id; refresh those too.
const viewForWriting = async (userId, needsClientIdOf) => {
  let { view } = await localView(userId);
  const stale = view && needsClientIdOf && !needsClientIdOf(view);
  if ((!view || stale) && navigator.onLine !== false) {
    await networkGet('/surveys');
    ({ view } = await localView(userId));
  }
  if (!view) {
    throw httpError(503, "This device doesn't have the survey data yet. Open the app once with signal, then try again.");
  }
  return view;
};

const write = async (kind, type, ref, payload) => {
  const user = cachedUser.get();
  if (!user?.id) throw httpError(401, 'Sign in again to save changes.');
  const config = RECORD_TYPES[type];
  const target = () => (kind === 'create' ? [config.parent, payload?.[config.fk]] : [type, ref]);
  const [lookupType, lookupRef] = target();
  const hasClientId = (view) => !lookupType || Boolean(findRecord(view, lookupType, lookupRef)?.node.client_id);
  const view = await viewForWriting(user.id, hasClientId);

  const operation = { op_id: newId(), op: kind, type };
  if (kind === 'create') {
    let parentClientId;
    if (config.parent) {
      const parent = findRecord(view, config.parent, payload?.[config.fk]);
      if (!parent?.node.client_id) throw httpError(404, 'The record this belongs to was not found on this device.');
      parentClientId = parent.node.client_id;
    }
    Object.assign(operation, { client_id: newId(), parent_client_id: parentClientId, data: recordData(type, payload) });
  } else {
    const entry = findRecord(view, type, ref);
    if (!entry?.node.client_id) throw httpError(404, 'This record was not found on this device.');
    Object.assign(operation, { client_id: entry.node.client_id, base_version: entry.node.version });
    if (kind === 'update') operation.data = recordData(type, payload);
  }

  const stored = await addToOutbox(user.id, operation);
  scheduleSync();
  if (kind === 'delete') return undefined;
  const { view: updated } = await localView(user.id);
  return deriveFromSurveys(`${config.path}/${stored.client_id}`, updated);
};

const recordApi = (type) => ({
  create: (data) => write('create', type, null, data),
  update: (id, data) => write('update', type, id, data),
  delete: (id) => write('delete', type, id),
});

export const surveyRecords = {
  ...recordApi('survey'),
  getAll: () => read('/surveys'),
  getById: (id) => read(`/surveys/${id}`),
};

export const transectRecords = {
  ...recordApi('transect'),
  getAllBySurvey: (surveyId) => read(`/surveys/${surveyId}/transects`),
  getById: (id) => read(`/transects/${id}`),
};

export const quadratRecords = {
  ...recordApi('quadrat'),
  getAll: () => read('/quadrats'),
  getAllByTransect: (transectId) => read(`/transects/${transectId}/quadrats`),
  getById: (id) => read(`/quadrats/${id}`),
};

export const matureTreeRecords = {
  ...recordApi('mature_tree'),
  getAll: () => read('/mature-trees'),
  getAllByQuadrat: (quadratId) => read(`/quadrats/${quadratId}/mature-trees`),
  getById: (id) => read(`/mature-trees/${id}`),
};

export const regenerationPlotRecords = {
  ...recordApi('regeneration_plot'),
  getAllByQuadrat: (quadratId) => read(`/quadrats/${quadratId}/regeneration-plots`),
  getById: (id) => read(`/regeneration-plots/${id}`),
};

export const regenerationCountRecords = {
  ...recordApi('regeneration_count'),
  getAll: () => read('/regeneration-counts'),
  getAllByPlot: (plotId) => read(`/regeneration-plots/${plotId}/regeneration-counts`),
  getById: (id) => read(`/regeneration-counts/${id}`),
};

export const disturbanceRecords = {
  ...recordApi('disturbance'),
  getAll: () => read('/disturbances'),
  getAllByQuadrat: (quadratId) => read(`/quadrats/${quadratId}/disturbances`),
  getById: (id) => read(`/disturbances/${id}`),
};
